// NOTE: Some of the UI elements in here are derived from other areas.
// However, those other areas have a lot of customizations (surcharge etc.)
// which is not relevant to this part of the system.
// Others, such as the ResponseFeedback, expect an input format which is
// very different from what we can obtain via the pax request lookup, or
// the transaction-by-id call.
//
// In the interest of avoiding unexpected changes, by carving out many
// IF conditionals and custom behaviors in the existing system areas, this
// will be all encompassing/isolated into this file, so it remains a standalone
// page, separate from the others for now.
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import _ from 'lodash'

import {
  CountrySelect,
  ErrorFeedback,
  Fieldset,
  HintToggleButton,
  If,
  InputText,
  Legend,
  PrimaryButton,
  Select,
  StateSelect,
} from '@paytrace/pt-skel/Component'
import BaseEvent from '@paytrace/pt-skel/Event'
import { DiscData } from '../Component'
import { AppCtx, AppCtxI } from '@paytrace/pt-skel/Context'
import Persistence from '@paytrace/pt-skel/Context/Persistence'
import { getFromQueryString } from '@paytrace/pt-skel/Helper/URL'
import { saveAndOrEmailButton, viewDetails, viewInvoice } from '../Component/ResponseFeedback'
import { PatternGuardedInputText } from '../Component/PatternGuardedInputText'
import { LegacyPermission } from '../Helper/HasPermission'
import SectionTitle from '../Component/SectionTitle'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import Event from '../Event'
import { SemiIntegratedHardwareIcon } from '../Component/Icons'
import * as T from '../types'
import { viewReceipt } from '../Helper/LegacyURLs'
// import BaseEvent from '@paytrace/pt-skel/Event'

interface Props {}

const self = Symbol('SemiIntegratedCard')

const SUPPORTED_SALE_TYPES = [
  { name: 'Sale', val: LegacyPermission.SALE },
  { name: 'Refund', val: LegacyPermission.REFUND },
]

interface Terminal {
  connected_at: string
  label: string
  serial_number: string
  terminal_id: string
  is_connected: boolean
  is_deactivated: boolean
}

interface PaxResponse {
  transactionId: string
  resultTxt: string
  resultCode: string
  message: string
  authCode: string
  approvedAmount: string
}

interface FeedbackI {
  transactionId: string // legacy_tranx_id
  resultTxt: string // OK
  resultCode: string // 000000 or 000100 for errors
  message: string // APPROVAL 010488
  authCode: string // 010488
  approvedAmountInCents: string // 123 = 1.23 (amount is in cents)
  merchantId: string
  goBackFn: () => void
  reprocessFn: () => void
  amount: string
  emails: string[]
}

function isUsingAutoAction (): boolean {
  return Boolean(getFromQueryString('auto-action'))
}

function formatTerminalLabel (label: string, serialNumber: string): string {
  return label + ' (s/n: ' + serialNumber + ')'
}

function getInitialReqFailureFeedback (
  message: string,
  merchantId: string,
  amount: string,
  goBackFn: () => void,
  reprocessFn: () => void,
) {
  return {
    transactionId: '',
    resultTxt: '',
    resultCode: '999999',
    message,
    authCode: '',
    approvedAmountInCents: '',
    merchantId,
    goBackFn,
    reprocessFn,
    amount,
    emails: [],
  }
}

interface FormBillingAddress {
  city: string
  state: string
  country: string
  street: string
  street2: string
  postal_code: string
}

interface FormData {
  amount: string
  auth_type: string
  invoice_number: string
  billing_name: string
  billing_address: FormBillingAddress
  terminal_uuid: string
}

const getInitialFormData = (): FormData => ({
  amount: '',
  auth_type: LegacyPermission.SALE,
  invoice_number: '',
  billing_name: '',
  billing_address: {
    city: '',
    state: '',
    country: '',
    street: '',
    street2: '',
    postal_code: '',
  },
  terminal_uuid: '',
})

function sortTerminals (terminals: Terminal[]): Terminal[] {
  const newestFirst = (key: string) => (a: Terminal, b: Terminal) =>
    moment(a[key] || '1970').isAfter(moment(b[key] || '1970')) ? -1 : 1

  return terminals
    .sort(newestFirst('connected_at'))
    .sort((a, _b) => a.is_connected ? -1 : 1)
    .filter(x => !x.is_deactivated)
}

function DiscDataFields (
  props: {
    items: T.DDPayload[],
    values: { [key: string]: string },
    setter: (x: { [key: string]: any }) => void,
}) {
  const { items, values, setter } = props

  if (items.length === 0) return <React.Fragment />

  // Derived from VirtualTerminal - we do this weird method of tracking
  // the values as object keys so that overwrites work properly (vs appending
  // to a discretionary_data[] array).  A transformer converts it with key/string
  // splitting at the end step, to take the input and make the DD values.
  return (
    <React.Fragment>
      {items.map((dd_props: T.DDPayload, i: number) => {
        const key = `disc_id_${dd_props.disc_id}`
        const val = values[key] || ''

        return (
          <DiscData
            key={i}
            dd_props={dd_props}
            value={val}
            onChange={(e) => {
              // Special handling for multi-select in here...
              // It needs the chosen value set as an array
              if ('M' === dd_props.type) {
                const anyE: any = e as any
                const selectedOptions: any = anyE.target.selectedOptions
                const chosen = Array
                  .from(selectedOptions)
                  .map(({ value }) => value)

                setter({ ...values, [key]: chosen })
              } else {
                // Everything else (normal select, input etc.)
                const text = e.target.value
                setter({ ...values, [key]: text })
              }
            }}
          />
        )
      })}
    </React.Fragment>
  )
}

function getDiscDataFromObject (
  values: { [key: string]: any }
): { disc_id: number, value: string }[] {
  const discData: { disc_id: number, value: string }[] = []

  Object.keys(values).forEach(key => {
    const m = key.match(/^disc_id_([0-9]+)$/)

    if (m) {
      const value = values[key]

      // When using multi-select, each value is stored as a db row, so
      // expand to an entry in discData for each pair
      if (Array.isArray(value)) {
        value.forEach(val => {
          discData.push({ disc_id: Number(m[1]), value: val })
        })
      } else {
        discData.push({ disc_id: Number(m[1]), value })
      }
    }
  })

  return discData
}

function terminalsToOpts (terminals: Terminal[]): { name: string, val: string }[] {
  if (terminals.length === 0) {
    return [{ name: 'Loading...', val: '' }]
  }

  // If your text editor doesn't show emojis (like mine), I apologize.
  // For whatever reason, it doesn't seem like HTML escape codes will render
  // in our <Select> list element, despite showing as literals in the HTML View-Source
  const greenCircle = '🟢'
  const redCircle = '🔴'
  // const yellowCircle = '🟡'

  return terminals.map(terminal => {
    const icon = terminal.is_connected ? greenCircle : redCircle
    const nick = terminal.label ?? 'No Nickname Chosen'
    return {
      name: `${icon} ${nick} (s/n: ${terminal.serial_number})`,
      val: terminal.terminal_id,
    }
  })
}

function getTerminalLabelByUuid (terminals: Terminal[], uuid: string): string {
  for (let i = 0; i < terminals.length; i++) {
    if (terminals[i].terminal_id === uuid) {
      return formatTerminalLabel(terminals[i].label, terminals[i].serial_number)
    }
  }

  return ''
}

function centsToDollars (amountInCents: string): string {
  if (!amountInCents) {
    return ''
  }

  // Handle values less than 100 ($1.00)
  const str = amountInCents.padStart(3, '0')
  const dollars = str.slice(0, -2)
  const cents = str.slice(-2)

  return `${dollars}.${cents}`
}

function Feedback (props: FeedbackI) {
  const amount = centsToDollars(props.approvedAmountInCents) || props.amount
  const orderId = props.transactionId
  const merchantId = props.merchantId
  const isSuccess = props.resultCode === '000000'
  const bannerType = isSuccess ? 'success' : 'error'
  let bannerMsg = isSuccess ? 'Approved' : 'Not Approved'

  if (isUsingAutoAction()) {
    bannerMsg += ' (void)'
  }

  const banner = (
    <div className={`uid-tranx-app-${bannerType} center ${bannerType} giant-text`}>
      {bannerMsg}
    </div>
  )
  const [availableEmails, setAvailableEmails] = useState<string[]>([...props.emails])
  const [emails, setEmails] = useState<string[]>([])

  useEffect(() => {
    PubSub.sub(
      self,
      Event.GET_CONFIGURED_EMAIL_LIST_RESPONSE,
      (response) => {
        const emails = [...props.emails]
        response.forEach((data: { ['email']: string }) => {
          emails.push(data.email)
        })
        setAvailableEmails([...emails])
      }
    )
  })

  useEffect(() => {
    const payload = {
      merchant_id: props.merchantId,
      email_type: '6',
    }
    PubSub.pub(Event.GET_CONFIGURED_EMAIL_LIST_REQUEST, payload)
  }, [props.merchantId])

  const emailClickHandler = (whom: string) => (e: any) => {
    // I must be missing the type, tsc says checked doesn't exist, but it does...
    if (e.target.checked) {
      setEmails([...emails, whom])
    } else {
      setEmails(emails.filter(email => email !== whom))
    }
  }

  const clickableEmail = (whom: string) => {
    return (
      <label style={{ cursor: 'pointer', display: 'block' }}>
        <input
          type='checkbox'
          className='uid-tranx-app-email'
          onClick={emailClickHandler(whom)} /> {whom}
      </label>
    )
  }

  const buildSuccessfullEmailMsg = (emails: string[]) => {
    let msg = ''
    emails.forEach((email: string) => {
      msg = msg + `The receipt was successfully emailed to ${email}.\n`
    })
    return msg
  }

  useEffect(() => {
    PubSub.sub(self, Event.SEND_SINGLE_EMAIL_RECEIPT_RES,
      (emailResponse) => {
        if (emailResponse.error_code === 0) {
          alert(buildSuccessfullEmailMsg(emailResponse.to.split(',')))
        } else {
          alert('There was a problem sending the email')
        }
      })
  }, [])

  return (
    <>
      <div className='float-right'>
        <HintToggleButton />
        <a className='help-link' href="https://paytrace.atlassian.net/wiki/spaces/HD/pages/2025914860/SemiIntegrated+Card">Help</a>
      </div>
      <h1>Insert or Tap Card</h1>
      <hr className='clear' />
        <Fieldset>
          <Legend title='Transaction Details'>
            <div className='float-right'>
              <button
                className='uid-tranx-app-details'
                onClick={() => viewDetails(orderId)}
              >Details</button>
              <button
                className='uid-tranx-app-receipt'
                onClick={() => viewReceipt(merchantId, orderId)}
              >Receipt</button>
              <button
                className='uid-tranx-app-invoice'
                onClick={() => viewInvoice(merchantId, orderId)}
              >Invoice</button>
            </div>
          </Legend>
          <div className='uid-tranx-app-clear clear'></div>
          {banner}
          <br />
          <table style={{ margin: 'auto' }}>
            <tr>
              <td style={{
                paddingRight: '20px',
                textAlign: 'right',
              }}>
                <strong>Amount</strong>
              </td>
              <td className="uid-tranx-app-transaction-amount">
                ${amount}
              </td>
            </tr>
            <tr>
              <td style={{ paddingRight: '20px', textAlign: 'right' }}>
                <strong>Approval&nbsp;Code</strong>
              </td>
              <td className='uid-tranx-app-approval-code'>{props.authCode}</td>
            </tr>

            <tr>
              <td style={{ paddingRight: '20px', textAlign: 'right' }}>
                <strong>Response</strong>
              </td>
              <td className='uid-tranx-app-response'>
                {props.message || props.resultTxt}{(props.resultCode === '000000' && ' - Approved and completed')}
              </td>
            </tr>

            <tr>
              <td style={{ paddingRight: '20px', textAlign: 'right' }}>
                <strong>Order&nbsp;ID</strong>
              </td>
              <td className='uid-tranx-app-order-id'>
                {orderId}
              </td>
            </tr>
            <tr><td>&nbsp;</td><td>&nbsp;</td></tr>

            <If isNot={isSuccess}>
              <button
                className='uid-tranx-app-reprocess-card'
                style={{ background: 'orange', color: '#fff' }}
                onClick={() => props.reprocessFn()}
              >&#x21BB; Retry Transaction</button>
            </If>

            <tr><td>&nbsp;</td><td>&nbsp;</td></tr>
            <If is={Boolean(orderId)}>
              <tr>
                <td style={{ paddingRight: '20px', textAlign: 'right' }}>
                  Email Receipts To
                </td>
                <td className='uid-tranx-app-available-emails'>
                  {availableEmails.map(clickableEmail)}
                </td>
              </tr>
              <tr>
                <td style={{ paddingRight: '20px', textAlign: 'right' }}>
                  &nbsp;
                </td>
                <td className='uid-tranx-app-save-or-email-button-container'>
                  {saveAndOrEmailButton(orderId, emails, false, -1, Number(merchantId))}
                </td>
              </tr>
            </If>
          </table>
          <button type="button" onClick={(_) => props.goBackFn()}>Go Back</button>
        </Fieldset>
    </>
  )
}

const STATUS_DEFAULT = 'Transmitting to Terminal'
const STATUS_HEADER_DEFAULT = 'Transaction in Progress'

function formDataChange (setter: (x: any) => any, path: string, val: string) {
  setter((prevState: FormData) => {
    const clone = { ...prevState }
    _.set(clone, path, val)
    return clone
  })
}

// TODO: Re-use this in VirtualTerminal file
function getEmailsFromContext (context: AppCtxI): string[] {
  const emails: string[] = []

  if (context.records.company.email) {
    emails.push(context.records.company.email)
  }
  if (context.records.company.email2) {
    emails.push(context.records.company.email2)
  }

  if (context.records.user.email) {
    emails.push(context.records.user.email as string)
  }

  return emails
}

export function SemiIntegratedCard (_props: Props) {
  const context = useContext(AppCtx)

  // Form values
  const [formData, setFormData] = useState<FormData>(getInitialFormData())

  // UI Values
  const [terminals, setTerminals] = useState<Terminal[]>([])
  const [isPending, setIsPending] = useState(false)
  const [requestId, setRequestId] = useState('')
  const [transactionId, setTransactionId] = useState('')
  const [status, setStatus] = useState(STATUS_DEFAULT)
  const [statusHeader, setStatusHeader] = useState(STATUS_HEADER_DEFAULT)
  const [feedback, setFeedback] = useState<FeedbackI | null>(null)
  const [discDataFields, setDiscDataFields] = useState<T.DDPayload[]>([])
  const [discDataValues, setDiscDataValues] = useState<{ [key: string]: string }>({})
  const [isLoadingDiscData, setIsLoadingDiscData] = useState(false)
  const [networkError, setNetworkError] = useState('')
  const pendingIconRef = useRef<HTMLDivElement>(null)

  // Conditional form render settings (based on merchant preferences)
  const viewElement: { [key: string]: boolean } = context.records.merchant.viewbits_dict || {}
  const isCardPresentEnforced = context.records.merchant.enforce_for_card_present || false
  const reqElement: { [key: string]: boolean } = {
    business_address: isCardPresentEnforced && (context.records.merchant.require_business_address || false),
    csc: isCardPresentEnforced && (context.records.merchant.require_csc || false),
    invoice_number: isCardPresentEnforced && (context.records.merchant.require_invoice_number || false),
    zip_code: isCardPresentEnforced && (context.records.merchant.require_zip_code || false),
  }
  let displayDropdown = context.records.merchant.display_drop_down
  if (displayDropdown === undefined) {
    displayDropdown = true
  }

  // Fixed values
  const emails: string[] = getEmailsFromContext(context)

  // Conditional based on product (surcharge)
  // FIXME: Do we need this stuff?  We won't be able to tell if card is or not until too late
  const canMerchantSurcharge = false
  const isCardSurchargeable = false

  const resetFeedback = useCallback(() => {
    setIsPending(false)
    setTransactionId('')
    setRequestId('')
    setFeedback(null)
    setStatus(STATUS_DEFAULT)
    setStatusHeader(STATUS_HEADER_DEFAULT)
  }, [])

  const goBack = useCallback(() => {
    // If we are on this page for the auto action feature (void)
    // the go back button should take us back to the void page, not
    // back to th bare form data, as it wouldn't make sense.
    if (isUsingAutoAction()) {
      window.location.href = '/void.pay'
    } else {
      resetFeedback()
      PubSub.pub(BaseEvent.NETWORK_ERROR_CLEAR)
    }
  }, [resetFeedback])

  const submit = useCallback((
    e?: React.FormEvent<HTMLFormElement>,
    formDataOverride?: FormData,
    labelOverride?: string,
  ): boolean => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    PubSub.pub(BaseEvent.NETWORK_ERROR_CLEAR)

    // Users shouldn't be getting here as it requires submitting almost
    // immediately on load, but it can happen during testing quite easily.
    if (isLoadingDiscData && !formDataOverride) {
      alert('Please wait, the form is still loading.')
      return false
    }

    const data = formDataOverride || formData
    resetFeedback()
    setIsPending(true)
    const label = labelOverride || getTerminalLabelByUuid(terminals, data.terminal_uuid)
    setStatus('Transmitting to Terminal: ' + label)

    const discData = getDiscDataFromObject(discDataValues)
    const saleDetails = {
      ...data, // good as-is, no casts needed (strings)
      billing_address: {
        ...data.billing_address, // good as-is, no casts needed (strings)
        postal_code: data.billing_address.postal_code.replace(/[^ A-Za-z0-9]/g, ''),
      },
      merchant_id: String(context.merchant),
      discretionary_data: discData,
    }

    PubSub.pub(
      Event.RUN_TERMINAL_SALE_REQUEST,
      data.terminal_uuid,
      data.auth_type,
      saleDetails,
    )

    return false
  }, [
    isLoadingDiscData,
    terminals,
    formData,
    discDataValues,
    context.merchant,
    resetFeedback,
  ])

  const shakeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Icon (fade in/out/shake) interval useEffect
  useEffect(() => {
    function stopInterval () {
      if (shakeIntervalRef.current) {
        clearInterval(shakeIntervalRef.current)
      }
    }

    function startInterval () {
      // Small shake effect for pending icon
      shakeIntervalRef.current = setInterval(() => {
        if (pendingIconRef.current) {
          pendingIconRef.current.style.opacity = '0.5'
          setTimeout(() => {
            if (pendingIconRef.current) {
              pendingIconRef.current.style.opacity = '1'
            }
          }, 1000)
        }
      }, 2000)
    }

    if (isPending) {
      startInterval()
    } else {
      stopInterval()
    }

    return () => {
      stopInterval()
    }
  }, [isPending])

  // Polling interval useEffect
  useEffect(() => {
    function stopInterval () {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }

    function startInterval () {
      // Periodically check if a requestId has records
      pollIntervalRef.current = setInterval(() => {
        if (!requestId) {
          console.debug('Missing request id, skipping...')
          return
        }
        console.debug('Checking status for: ' + requestId)

        PubSub.pub(Event.TERMINAL_REQUEST_STATUS, requestId)
      }, 2000)
    }

    if (isPending && requestId && !feedback) {
      startInterval()
    } else {
      stopInterval()
    }

    return () => {
      stopInterval()
    }
  }, [isPending, requestId, feedback])

  useEffect(() => {
    const myself = Symbol('SELF_NETWORK_ERROR')

    PubSub.sub(myself, Event.NETWORK_ERROR, (data) => {
      setNetworkError(data.toString())
    })

    PubSub.sub(myself, BaseEvent.NETWORK_ERROR_CLEAR, () => {
      setNetworkError('')
    })

    return () => {
      PubSub.unsub(myself)
    }
  }, [])

  // Set up subscriptions tied to top level render
  useEffect(() => {
    // Update our status if we see a change in it
    PubSub.sub(self, Event.TERMINAL_REQUEST_STATUS_RESPONSE, (data) => {
      const currentStatus = _.get(data, 'status.status', '')
      const firstResponse: PaxResponse = _.get(data, 'responses[0].payload', null)

      // This should only ever happen if broadpos is incorrectly configured on
      // the terminal, and if such a thing occurs, we need the user to see the
      // more specific diagnostic message/error.
      // Sample:
      //   Failure to process, likely missing BROADPOS!: java.lang.Exception:
      //   SDK_Error (msg: SDK_Error (code: ERROR / msg: ERROR:BROADPOS NOT FOUND))
      const altResponse: string = (typeof firstResponse) === 'string' ? String(firstResponse) : ''

      // NOTE: Consider querying trans-search here for the polling?
      // Then we can get the post-synced result, rather than the pax result
      // which may or may not have successfully synced to other systems.
      if (currentStatus === 'processed' && firstResponse) {
        setStatus(STATUS_DEFAULT)
        setFeedback({
          transactionId, // from state
          merchantId: String(context.merchant),
          resultTxt: firstResponse.resultTxt,
          resultCode: firstResponse.resultCode,
          message: firstResponse.message || altResponse,
          authCode: firstResponse.authCode,
          approvedAmountInCents: firstResponse.approvedAmount,
          goBackFn: goBack,
          reprocessFn: submit,
          amount: formData.amount,
          emails,
        })
        setStatusHeader('Transaction Complete!')
        return
      }

      const label = getTerminalLabelByUuid(terminals, formData.terminal_uuid)

      if (currentStatus === 'received') {
        setStatus('Waiting on Terminal: ' + label)
        return
      }

      if (currentStatus === 'pending') {
        setStatus('Initializing Terminal: ' + label)
      }
    })

    // Listen for if the sale has completed
    PubSub.sub(
      self,
      Event.RUN_TERMINAL_SALE_RESPONSE,
      ({ request_id, transaction_id }: { request_id: string, transaction_id: string }
      ) => {
        if (!request_id || !transaction_id) {
          setFeedback(getInitialReqFailureFeedback(
            'Failed to run terminal sale: An error has occurred. Please contact support.',
            String(context.merchant),
            formData.amount,
            goBack,
            submit,
          ))
        } else {
          setRequestId(request_id)
          setTransactionId(transaction_id)
          // setStatus('Request ID: ' + request_id)
        }
      })

    PubSub.sub(self, Event.TERMINAL_LIST_RESPONSE, (terminals: Terminal[]) => {
      setTerminals(sortTerminals(terminals))

      // Default to the 0th terminal, as it's the most likely candidate (still connected)
      if (terminals.length > 0) {
        formDataChange(setFormData, 'terminal_uuid', terminals[0].terminal_id)
      }
    })

    PubSub.sub(self, Event.GET_DISC_DATA_RESPONSE, (discData: T.DDPayload[]) => {
      // Bubble up the dynamic field assignments
      const discDataState: { [key: string]: string } = {}

      discData.forEach((dd_props: T.DDPayload) => {
        const key = `disc_id_${dd_props.disc_id}`
        discDataState[key] = ''
      })

      setDiscDataFields(discData)
      setIsLoadingDiscData(false)
    })

    return () => {
      PubSub.unsub(self)
    }
  }, [
    context.merchant,
    requestId,
    pendingIconRef,
    formData,
    terminals,
    transactionId,
    emails,
    goBack,
    submit,
  ])

  // Fire off pubs - don't do in preceding useEffect to avoid recursion due to dep array
  useEffect(() => {
    const autoAction = getFromQueryString('auto-action')
    const urlCheck = getFromQueryString('check')
    const autoActionData = Persistence.getItem('semi-integrated-auto-action') || {}

    if (autoAction === 'void' && String(urlCheck) === String(autoActionData.check)) {
      // Handle a special void case
      // We don't need to load this actual data, so skip/match to what we have.
      setIsLoadingDiscData(false)
      setTerminals([
        {
          connected_at: '',
          label: autoActionData.terminalLabel,
          serial_number: autoActionData.terminalSerialNumber,
          terminal_id: autoActionData.terminalId,
          is_connected: true,
          is_deactivated: false,
        },
      ])
      const terminalLabelOverride = formatTerminalLabel(
        autoActionData.terminalLabel,
        autoActionData.terminalSerialNumber,
      )

      // Void comes through similar to this - to make it compatible with the existing
      // SDK calls, we are going to take a shortcut and just combine the target
      // terminal ID and transaction ID into a single value for the receiver.
      // See the terminal_uuid concatenation.
      // POST /v3/card/void/terminal/bd21dbe1-0ad2-3322-adff-1a3f991f4850/127493716
      const autoActionFormData = {
        amount: autoActionData.amount,
        auth_type: LegacyPermission.VOID,
        invoice_number: '',
        billing_name: '',
        billing_address: {
          city: '',
          state: '',
          country: '',
          street: '',
          street2: '',
          postal_code: '',
        },
        terminal_uuid: autoActionData.terminalId + '/' + autoActionData.transactionId,
      }
      setFormData(autoActionFormData)
      submit(undefined, autoActionFormData, terminalLabelOverride)
    } else {
      // Business as usual (load the form for sale/refunds)
      PubSub.pub(Event.TERMINAL_LIST_REQUEST, { merchantId: String(context.merchant) })

      setIsLoadingDiscData(true)
      PubSub.pub(Event.GET_DISC_DATA_REQUEST, { paytraceId: String(context.merchant) })
    }

    return () => undefined
  }, [context.merchant])

  const updateTerminalList = useCallback(() => {
    setTerminals([])
    PubSub.pub(Event.TERMINAL_LIST_REQUEST, { merchantId: String(context.merchant) })
    return false
  }, [context.merchant])

  if (feedback) {
    return (<Feedback {...feedback} />)
  }

  if (isPending) {
    return (
      <>
        <div className='float-right'>
          <HintToggleButton />
          <a className='help-link' href='https://paytrace.atlassian.net/wiki/spaces/HD/pages/2025914860/SemiIntegrated+Card'>Help</a>
        </div>
        <h1>Insert or Tap Card</h1>
        <hr className='clear' />
        <form onSubmit={(_) => false}>
          <Fieldset>
            <Legend title='Transaction Details' />
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ transition: 'opacity 1s ease-in-out' }} ref={pendingIconRef}>
                <SemiIntegratedHardwareIcon />
              </div>
              <h1>{statusHeader}</h1>
              <pre style={{
                background: '#ccffcc',
                color: '#003300',
                padding: '20px',
                fontSize: '14px',
                maxWidth: '1180px',
                overflow: 'ellipsis',
                textAlign: 'left',
              }}>{status}</pre>
              <If isNot={isUsingAutoAction()}>
                <button type="button" onClick={(_) => goBack()}>Go Back</button>
              </If>
            </div>
          </Fieldset>
        </form>
      </>
    )
  }

  return (
    <>
      <div className='float-right'>
        <HintToggleButton />
        <a className='help-link' href="https://paytrace.atlassian.net/wiki/spaces/HD/pages/2025914860/SemiIntegrated+Card">Help</a>
      </div>
      <h1>Insert or Tap Card</h1>
      <hr className='clear' />
      <form onSubmit={submit}>
        <Fieldset>
          <Legend title='Transaction Details' />
          <SectionTitle title='Transaction Details'/>
          <Select
            className='uid-transaction-type'
            label='Transaction Type'
            required={true}
            options={SUPPORTED_SALE_TYPES}

            onChange={(e) => formDataChange(setFormData, 'auth_type', e.target.value)}
            value={formData.auth_type}
            hintText='Please select what type of transaction you would like to process from the drop down menu. Options are sale, authorization, forced sale (a sale that was authorized offline), and refund.'
          />
          <If is={!!viewElement.invoice_number}>
            <InputText
              maxLength={50}
              className='uid-invoice-number default-focus'
              label='Invoice Number'
              onChange={(e) => formDataChange(setFormData, 'invoice_number', e.target.value)}
              value={formData.invoice_number}
              required={reqElement.invoice_number}
              placeholder={reqElement.invoice_number ? '' : 'Optional'}
              hintText='Please provide an invoice number for this transaction that may contain up to 50 characters of free text.  The invoice number field may be used to provide an identifier for this transaction that relates to your accounting or inventory system, if applicable.'
              autoFocus={true}
            />
          </If>

          <PatternGuardedInputText
            className='uid-amount default-focus'
            onChange={(e) => formDataChange(setFormData, 'amount', e.target.value)}
            name='amount'
            label='Amount'
            pattern='^([0-9]{0,17}|([0-9]{0,15}\.[0-9]{0,2}))$'
            value={formData.amount}
            placeholder='0.00'
            autoComplete='off'
            required
            hintText='Enter this amount as it would be entered on a calculator, with decimals, if necessary.'
            autoFocus={true}
          />

          <SectionTitle title='Billing Address'/>
          <If is={!!viewElement.billing_name}>
            <InputText
              maxLength={50}
              className='uid-billing-name'
              label='Name'
              onChange={(e) => formDataChange(setFormData, 'billing_name', e.target.value)}
              value={formData.billing_name}
              placeholder='Optional'
              hintText="Please provide the customer's name, preferrably as it is shown on the card."
            />
          </If>

          <If is={!!viewElement.billing_address}>
            <PatternGuardedInputText
              className='uid-billing-address'
              label='Address Line 1'
              pattern="^([a-zA-Z0-9\/\.\-_,#'&+\s\xc0-\xff]){0,75}$"
              onChange={(e) => formDataChange(setFormData, 'billing_address.street', e.target.value)}
              value={formData.billing_address.street}
              required={reqElement.business_address}
              placeholder={reqElement.business_address ? '' : 'Optional'}
              hintText="Please provide your customer's billing address where their bank statement is delivered. We strongly recommend providing a billing address as this information will benefit your transaction qualification.  Additionally, the billing address is validated via AVS (Address Verification System) to help prevent fraud."
            />
          </If>
          <If is={!!viewElement.billing_address2}>
            <PatternGuardedInputText
              className='uid-billing-address2'
              label='Address Line 2'
              pattern="^([a-zA-Z0-9\/\.\-_,#'&+\s\xc0-\xff]){0,50}$"
              onChange={(e) => formDataChange(setFormData, 'billing_address.street2', e.target.value)}
              value={formData.billing_address.street2}
              placeholder='Optional'
              hintText="Please provide any additional elements of your customer's billing address."
            />
          </If>
          <br />
          <If is={!!viewElement.billing_city}>
            <PatternGuardedInputText
              className='uid-billing-city'
              label='City'
              pattern="^[A-Za-z \.'-]{0,50}$"
              onChange={(e) => formDataChange(setFormData, 'billing_address.city', e.target.value)}
              value={formData.billing_address.city}
              placeholder='Optional'
              hintText="Please provide the city where your customer's billing address is located."
            />
          </If>
          <If is={!!viewElement.billing_state}>
            <If is={!!displayDropdown}>
              <StateSelect
                className='uid-billing-state'
                label='State'
                name='billing_state'
                onChange={(e) => formDataChange(setFormData, 'billing_address.state', e.target.value)}
                value={formData.billing_address.state}
                required={isCardSurchargeable && canMerchantSurcharge}
                placeholder={isCardSurchargeable ? '' : 'Optional'}
                hintText="Please select the state where your customer's Billing address is located."
              />
            </If>
            <If is={!displayDropdown}>
              <PatternGuardedInputText
                className='uid-billing-state small-input'
                label='State'
                pattern='^[A-Za-z]{0,2}$'
                onChange={(e) => formDataChange(setFormData, 'billing_address.state', e.target.value)}
                value={formData.billing_address.state}
                required={isCardSurchargeable && canMerchantSurcharge}
                placeholder={isCardSurchargeable ? '' : 'Optional'}
                hintText="Please select the state where your customer's billing address is located."
              />
            </If>
          </If>
          <If is={!!viewElement.billing_zip}>
            <PatternGuardedInputText
              className='uid-billing-zip small-input'
              label='Zip Code'
              pattern='^[ A-Za-z0-9-]{0,10}$'
              onChange={(e) => formDataChange(setFormData, 'billing_address.postal_code', e.target.value)}
              value={formData.billing_address.postal_code}
              required={reqElement.zip_code}
              placeholder={reqElement.zip_code ? '' : 'Optional'}
              hintText="Please provide your customer's billing ZIP code where their bank statement is delivered. We strongly recommend providing a billing ZIP code as this information will benefit your transaction qualification.  Additionally, the billing ZIP code is validated via AVS (Address Verification System) to help prevent fraud."
            />
          </If>
          <If is={!!viewElement.billing_country}>
            <CountrySelect
              className='uid-billing-country'
              label='Country'
              name='billing_country'
              onChange={(e) => formDataChange(setFormData, 'billing_address.country', e.target.value)}
              value={formData.billing_address.country}
              placeholder='Optional'
              hintText="Please select the country where your customer's billing address is located."
            />
          </If>

          <DiscDataFields
            items={discDataFields}
            values={discDataValues}
            setter={setDiscDataValues}
          />

          <SectionTitle title='Terminal'/>
          <div>
            <div style={{ display: 'inline-block' }}>
              <Select
                className='uid-terminal-uuid'
                label='Select Terminal'
                options={terminalsToOpts(terminals)}
                onChange={(e) => formDataChange(setFormData, 'terminal_uuid', e.target.value)}
                value={formData.terminal_uuid}
                hintText='Choose the target terminal.'
              />
            </div>
            <button
              type="button"
              onClick={(_) => updateTerminalList()}
              title='Update list'
              style={{
                background: '#fff',
                color: 'blue',
                display: 'inline-block',
                border: '0px',
                fontSize: '20px',
                marginLeft: '-10px',
              }}>
              &#x21BB;
            </button>
          </div>

          <div className='spacer'><ErrorFeedback name='root' /></div>
          <hr />

          <div style={{ textAlign: 'center' }}>
            <PrimaryButton style={{ margin: 'auto' }} className='uid-process'>
              &#x1F4B3; Complete Payment on Terminal
            </PrimaryButton>
          </div>
        </Fieldset>
        <If is={networkError.length > 0}>
          <div className='error center net-error-feedback'>
            {networkError}
          </div>
        </If>
      </form>
    </>
  )
}

export default SemiIntegratedCard
